import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BadgeCheck, MapPin, TestTube2, Package, CalendarDays, Droplets, Home, Coffee } from 'lucide-react';
import { patientApi } from '../api';

export default function PatientLabProfile() {
  const { labProfileId } = useParams<{ labProfileId: string }>();
  const navigate = useNavigate();
  const [lab, setLab] = useState<any>(null);
  const [tab, setTab] = useState<'tests' | 'packages' | 'slots'>('tests');
  const [tests, setTests] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [slotDate, setSlotDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!labProfileId) return;
    let alive = true;
    patientApi.lab.getLabProfile(labProfileId)
      .then((d: any) => { if (alive) setLab(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [labProfileId]);

  useEffect(() => {
    if (!labProfileId) return;
    let alive = true;
    setLoading(true);
    if (tab === 'tests') {
      patientApi.lab.getLabTests(labProfileId)
        .then((d: any) => { if (alive) setTests(Array.isArray(d) ? d : d?.tests || []); })
        .catch(() => { if (alive) setTests([]); })
        .finally(() => { if (alive) setLoading(false); });
    } else if (tab === 'packages') {
      patientApi.lab.getLabPackages(labProfileId)
        .then((d: any) => { if (alive) setPackages(Array.isArray(d) ? d : d?.packages || []); })
        .catch(() => { if (alive) setPackages([]); })
        .finally(() => { if (alive) setLoading(false); });
    } else {
      patientApi.lab.getLabSlots(labProfileId, { date: slotDate })
        .then((d: any) => { if (alive) setSlots(Array.isArray(d) ? d : d?.slots || []); })
        .catch(() => { if (alive) setSlots([]); })
        .finally(() => { if (alive) setLoading(false); });
    }
    return () => { alive = false; };
  }, [labProfileId, tab, slotDate]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Lab Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{lab?.labName || lab?.name || 'Lab Profile'}</h1>
            <p className="text-sm text-gray-500 mt-1">Lab services on NeoHealthCard</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {lab?.accreditation && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                <BadgeCheck className="w-3 h-3" />{lab.accreditation}
              </span>
            )}
            {lab?.verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                Verified
              </span>
            )}
          </div>
        </div>
        {lab?.address && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
            <MapPin className="w-3.5 h-3.5" />
            {typeof lab.address === 'string' ? lab.address : `${lab.address.line1 || ''}, ${lab.address.city || lab.city || ''}`}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {(['tests', 'packages', 'slots'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : tab === 'tests' ? (
        tests.length === 0 ? (
          <EmptyState icon={TestTube2} text="No tests available" />
        ) : (
          <div className="space-y-2">
            {tests.map((test: any, i: number) => (
              <div key={test.id || i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{test.testName || test.name}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {test.testCode && <span className="text-[10px] text-gray-400 font-mono">{test.testCode}</span>}
                    {test.sampleType && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-500">
                        <Droplets className="w-3 h-3" />{test.sampleType}
                      </span>
                    )}
                    {test.fasting && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-orange-50 text-orange-600 border border-orange-200">
                        <Coffee className="w-3 h-3" />Fasting
                      </span>
                    )}
                    {test.homeCollection && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                        <Home className="w-3 h-3" />Home
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm font-bold text-teal-700 shrink-0">
                  {test.price != null ? `₹${Number(test.price).toLocaleString('en-IN')}` : '—'}
                </span>
              </div>
            ))}
          </div>
        )
      ) : tab === 'packages' ? (
        packages.length === 0 ? (
          <EmptyState icon={Package} text="No packages available" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {packages.map((pkg: any, i: number) => (
              <div key={pkg.id || i} className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-2">{pkg.packageName || pkg.name}</h3>
                {pkg.tests && (
                  <ul className="text-xs text-gray-500 space-y-1 mb-3">
                    {pkg.tests.slice(0, 5).map((t: any, j: number) => (
                      <li key={j} className="flex items-center gap-1"><TestTube2 className="w-3 h-3 text-teal-400" />{typeof t === 'string' ? t : t.testName || t.name}</li>
                    ))}
                    {pkg.tests.length > 5 && <li className="text-gray-400">+{pkg.tests.length - 5} more</li>}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-teal-700">
                    ₹{Number(pkg.price || pkg.discountedPrice || 0).toLocaleString('en-IN')}
                  </span>
                  {pkg.originalPrice && pkg.originalPrice > (pkg.price || pkg.discountedPrice || 0) && (
                    <span className="text-xs text-gray-400 line-through">₹{Number(pkg.originalPrice).toLocaleString('en-IN')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-4">
          <input
            type="date"
            value={slotDate}
            onChange={e => setSlotDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          {slots.length === 0 ? (
            <EmptyState icon={CalendarDays} text="No slots for this date" />
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {slots.map((slot: any, i: number) => {
                const available = slot.available !== false && slot.status !== 'FULL';
                return (
                  <div
                    key={slot.id || i}
                    className={`text-center py-2.5 px-2 rounded-lg text-xs font-medium border ${
                      available
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-gray-100 border-gray-200 text-gray-400'
                    }`}
                  >
                    {slot.time || slot.startTime || slot.label || `Slot ${i + 1}`}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Book Appointment CTA */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate(`/patient/lab-booking/${labProfileId}`)}
          className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Book Appointment
        </button>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">{text}</p>
    </div>
  );
}
